<template>
  <div
    class="pill-select"
    :class="{ open: isOpen, disabled }"
    @keydown.stop.prevent="onKeydown"
    ref="root"
  >
    <button
      class="pill-trigger"
      type="button"
      :disabled="disabled"
      :aria-expanded="isOpen ? 'true' : 'false'"
      aria-haspopup="listbox"
      @click="toggle"
    >
      <div class="d-flex align-items-center">
        <v-icon v-if="selected.icon" :color="selected.color" size="20" class="mr-2">
          {{ selected.icon }}
        </v-icon>

        <span class="label mr-2">
          {{ selected ? selected[labelKey] : placeholder }}
        </span>

        <v-icon color="#68686E" size="21">$CaretDown</v-icon>
      </div>
    </button>

    <transition name="fade">
      <ul
        v-if="isOpen"
        class="menu"
        role="listbox"
        :aria-activedescendant="'opt-' + focusedIndex"
        tabindex="-1"
        ref="menu"
      >
        <li
          v-for="(item, i) in items"
          :key="item[valueKey]"
          class="option"
          :id="'opt-' + i"
          :aria-selected="isSelected(item) ? 'true' : 'false'"
          role="option"
          :class="{ focused: i === focusedIndex, selected: isSelected(item) }"
          @mouseenter="focusedIndex = i"
          @mousedown.prevent="select(item)"
        >
          <v-icon v-if="item.icon" :color="item.color" size="20" class="mr-2">
            {{ item.icon }}
          </v-icon>

          <span class="label">
            {{ item[labelKey] }}
          </span>

          <svg v-if="isSelected(item)" class="check" width="16" height="16" viewBox="0 0 24 24">
            <path d="M9 16.2l-3.5-3.5L4 14.2l5 5 11-11-1.5-1.5z" />
          </svg>
        </li>
      </ul>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'MixSelect',
  props: {
    value: { required: false },
    items: {
      type: Array,
      required: true
    },
    labelKey: { type: String, default: 'label' },
    valueKey: { type: String, default: 'value' },
    colorKey: { type: String, default: 'color' },
    placeholder: { type: String, default: 'Selecionar...' },
    disabled: { type: Boolean, default: false }
  },
  data() {
    return { isOpen: false, focusedIndex: 0 }
  },
  computed: {
    selected() {
      const key = this.valueKey
      return this.items.find(it => it[key] === this.value) || null
    }
  },
  watch: {
    isOpen(v) {
      if (v) {
        const idx = this.items.findIndex(it => this.isSelected(it))
        this.focusedIndex = idx >= 0 ? idx : 0

        this.$nextTick(() => {
          this.$refs.menu && this.$refs.menu.focus()

          setTimeout(() => {
            document.addEventListener('click', this.onClickOutside)
          }, 0)
        })
      } else {
        document.removeEventListener('click', this.onClickOutside)
      }
    }
  },
  methods: {
    toggle() {
      if (!this.disabled) this.isOpen = !this.isOpen
    },
    select(item) {
      this.$emit('input', item[this.valueKey])
      this.$emit('change', item)

      this.isOpen = false
    },
    isSelected(item) {
      return item[this.valueKey] === this.value
    },
    onClickOutside(e) {
      if (!this.$refs.root.contains(e.target)) this.isOpen = false
    },
    onKeydown(e) {
      if (this.disabled) return

      if (!this.isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
        this.isOpen = true;
        return
      }

      switch (e.key) {
        case 'ArrowDown':
          this.focusedIndex = (this.focusedIndex + 1) % this.items.length
          break
        case 'ArrowUp':
          this.focusedIndex = (this.focusedIndex - 1 + this.items.length) % this.items.length
          break
        case 'Enter':
        case ' ':
          this.select(this.items[this.focusedIndex])
          break
        case 'Escape':
          this.isOpen = false
          break
      }
    }
  }
}
</script>

<style scoped>
.pill-select {
  position: relative;
  display: inline-block;
}

.pill-select.disabled {
  opacity: .6;
  pointer-events: none;
}

.pill-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  border: 0;
  border-radius: 7px;
  padding: 8px 12px;
  background: #F4F4F6;
  cursor: pointer;
  font-weight: 500;
}

.pill-trigger .icon {
  --size: 20px;
  width: var(--size);
  height: var(--size);
  display: inline-grid;
  place-items: center;
  border-radius: 999px;
  background: #fff;
  outline: 2px solid var(--icon-color, #9e9e9e);
}

.pill-trigger .icon .svg {
  width: 14px;
  height: 14px;
  display: inline-block;
}

.pill-trigger .icon .txt {
  font-size: 12px;
  line-height: 1;
}

.pill-trigger .caret {
  opacity: .6;
  margin-left: 2px;
}

.menu {
  position: absolute;
  z-index: 10;
  min-width: 130px;
  margin-top: 6px;
  padding: 3px;
  background: #fff;
  border: 1px solid #e7e7ea;
  border-radius: 12px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, .08);
}

.option {
  display: flex;
  align-items: center;
  cursor: pointer;
  margin: 5px;
  padding: 8px 10px;
  border-radius: 10px;
}

.option .icon {
  --size: 22px;
  width: var(--size);
  height: var(--size);
  display: inline-grid;
  place-items: center;
  border-radius: 999px;
  background: #fff;
  outline: 2px solid var(--icon-color, #9e9e9e);
}

.option .svg {
  width: 14px;
  height: 14px;
  display: inline-block;
}

.option .txt {
  font-size: 12px;
}

.option .check {
  margin-left: auto;
  opacity: .8;
}

.option:hover, .option.focused {
  background: #f5f6f8;
}

.option.selected {
  background: #eef7ff;
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .12s ease;
}

.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
